import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Timesheet } from 'src/app/models/Timesheet';

@Component({
    selector: 'app-reports-modal',
    templateUrl: './reports-modal.component.html',
    styleUrls: ['./reports-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ReportsModalComponent {
    @Input() reportType: 'departments' | 'overtime' | 'billed' = 'departments';
    @Input() timesheets: Timesheet[] = [];
    @Input() departments: Array<{ key: string; label: string }> = [];

    constructor(public activeModal: NgbActiveModal) {}

    selectedTimesheet: Timesheet | null = null;
    projectBreakdown: Array<{ project: string; hours: number }> = [];

    getTimesheetPercentageTotals(type: string): number {
        const allSlots = (this.timesheets ?? []).reduce((acc: any[], ts: any) => acc.concat(ts.timeslots || []), []);
        const totalMinutes = allSlots.reduce((sum: number, slot: any) => sum + (slot.minutes || 0), 0);
        const typeMinutes = allSlots.filter((slot: any) => slot.project?.projectType === type).reduce((sum: number, slot: any) => sum + (slot.minutes || 0), 0);

        if (totalMinutes === 0) return 0;
        const percentage = (typeMinutes / totalMinutes) * 100;
        return Math.round(percentage * 100) / 100;
    }

    // Format minutes to hours label with one decimal and comma decimal separator, e.g. '100,0u'
    private formatMinutesAsHoursLabel(minutes: number): string {
        const hours = (minutes || 0) / 60;
        if (!isFinite(hours) || isNaN(hours)) return '0,0u';
        return `${hours.toFixed(1).replace('.', ',')}u`;
    }

    // Compute billed and total minutes per department
    getBilledByDepartment(): Array<{
        key: string;
        label: string;
        totalMinutes: number;
        billedMinutes: number;
        percent: string;
        totalLabel: string;
        billedLabel: string;
    }> {
        const allSlots = (this.timesheets ?? []).reduce((acc: any[], ts: any) => acc.concat(ts.timeslots || []), []);
        const results: Array<any> = [];
        for (const dept of this.departments || []) {
            const deptSlots = allSlots.filter((s: any) => s.project?.projectType === dept.key);
            const totalMinutes = deptSlots.reduce((sum: number, slot: any) => sum + (slot.minutes || 0), 0);
            const billedMinutes = deptSlots.filter((s: any) => s.billed).reduce((sum: number, slot: any) => sum + (slot.minutes || 0), 0);
            const percentRaw = totalMinutes > 0 ? (billedMinutes / totalMinutes) * 100 : 100;
            const percent = !isFinite(percentRaw) || isNaN(percentRaw) ? '100,0' : percentRaw.toFixed(1).replace('.', ',');
            results.push({
                key: dept.key,
                label: dept.label,
                totalMinutes,
                billedMinutes,
                percent,
                totalLabel: this.formatMinutesAsHoursLabel(totalMinutes),
                billedLabel: this.formatMinutesAsHoursLabel(billedMinutes),
            });
        }
        // sort descending by percent billed
        return results.sort((a, b) => Number(b.percent.replace(',', '.')) - Number(a.percent.replace(',', '.')));
    }

    // Sort departments descending by percentage total
    getDepartmentsSorted(): Array<{ key: string; label: string }> {
        return (this.departments || []).slice().sort((a, b) => this.getTimesheetPercentageTotals(b.key) - this.getTimesheetPercentageTotals(a.key));
    }

    getOverUnderEntries(): Array<{
        tsUuid: string;
        employee: string;
        totalHours: number;
        requiredHours: number;
        differenceHours: number;
        type: 'Overuren' | 'Onderuren';
    }> {
        if (!this.timesheets) return [];
        return (
            this.timesheets
                .map((ts) => {
                    const totalHours = Math.round((Timesheet.getTotalMinutes(ts) / 60) * 100) / 100;
                    const requiredHours = ts.hoursRequired ?? 0;
                    const differenceHours = Math.round((totalHours - requiredHours) * 100) / 100;
                    return {
                        tsUuid: ts.uuid,
                        employee: `${ts.employee?.firstName ?? ''} ${ts.employee?.lastName ?? ''}`.trim(),
                        totalHours,
                        requiredHours,
                        differenceHours,
                        type: (differenceHours > 0 ? 'Overuren' : 'Onderuren') as 'Overuren' | 'Onderuren',
                    };
                })
                .filter((e) => e.differenceHours !== 0)
                // Sort descending from largest difference to smallest
                .sort((a, b) => Math.abs(b.differenceHours) - Math.abs(a.differenceHours))
        );
    }

    showBreakdown(tsUuid: string) {
        const ts = (this.timesheets || []).find((t) => t.uuid === tsUuid) || null;
        this.selectedTimesheet = ts;
        if (!ts) {
            this.projectBreakdown = [];
            return;
        }
        const totals = new Map<string, number>();
        for (const slot of ts.timeslots || []) {
            const title = slot.project?.title ?? '—';
            const minutes = slot.minutes ?? 0;
            totals.set(title, (totals.get(title) ?? 0) + minutes);
        }
        this.projectBreakdown = Array.from(totals.entries())
            .map(([project, minutes]) => ({ project, hours: Math.round((minutes / 60) * 100) / 100 }))
            .sort((a, b) => b.hours - a.hours);
    }
}
